import React, { useEffect } from "react";
import Head from "next/head";
import Skeleton from "@material-ui/lab/Skeleton";
import merge from "lodash/merge";
import ProfileCard from "components/profile-card";
import ProfileModal from "components/profile-modal";
import HomeSkeleton from "components/skeleton/home-skeleton";
import { useActionBoard } from "components/action-board";
import { IProfile } from "features/profile/type";
import { useProfileList } from "features/profile/hooks";
import useOnScreen from "hooks/useOnScreen";
import { WEB_NAME } from "lib/constants";
import { useAppSelector } from "store";
import { wrapper } from "store";
import { GetServerSideProps } from "next";
import { updateDefault } from "features/seo/seoSlice";

/* eslint-disable @typescript-eslint/no-empty-interface */
interface Props {}

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  (store) => async () => {
    store.dispatch(updateDefault({}));
    return {
      props: {},
    };
  }
);

const Search: React.FC<Props> = () => {
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(loadMoreRef);
  React.useEffect(() => {
    if (!isLoadingMore && isOnScreen) {
      setSize(size + 1);
    }
  }, [isOnScreen]);

  const searchFor = useAppSelector((state) => state.app.searchKey);
  const {
    filter,
    sort,
    handleFilterChange,
    setSort,
    SearchActionBoard,
  } = useActionBoard({});
  const [extendFilter, setExtendFilter] = React.useState(
    merge({}, filter, { full_name: searchFor })
  );

  React.useEffect(() => {
    setExtendFilter(merge({}, filter, { full_name: searchFor }));
  }, [filter, searchFor]);

  const {
    profiles,
    isLoading,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    error,
    size,
    setSize,
    total,
  } = useProfileList(extendFilter, sort);

  const [profile, setProfile] = React.useState<IProfile | null>(null);

  const onCardClick = React.useCallback((profile: IProfile) => {
    setProfile(profile);
  }, []);

  /* useEffect(() => { */
  /*   if (!profile) { */
  /*     history.replaceState({}, "", "/tim-kiem"); */
  /*   } else { */
  /*     history.replaceState({}, "", `/${profile?.influzeeID}`); */
  /*   } */
  /* }, [profile]); */

  return (
    <>
      <Head>
        <title>{WEB_NAME}-Tìm kiếm</title>
      </Head>
      <div className="sm:mt-48 w-full">
        <div className="fixed w-screen top-20 left-0 pl-67.5 pr-7.5 bg-white z-40 pb-7.5">
          <SearchActionBoard
            searchFor={searchFor}
            totalResult={total}
            filter={filter}
            sort={sort}
            handleFilterChange={handleFilterChange}
            setSort={setSort}
          />
        </div>
        {error ? (
          <div className="flex flex-col justify-center w-100 px-80 pt-8">
            <img src="/assets/sidemenu-logo/error.svg" />
            <h3 className="text-center text-xl font-bold mb-2 mt-8">
              Oops! Chúng tôi đang cố gắng thử lại!
            </h3>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-3 home-web-md:grid-cols-4 home-web-xl:grid-cols-5 home-web-2xl:grid-cols-6 gap-7 home-container">
            {isLoading ? (
              <HomeSkeleton />
            ) : profiles?.length === 0 ? (
              <div className="flex flex-col shadow-lg rounded-2xl relative overflow-hidden">
                <div className="p-4">
                  <div className="w-full relative">
                    <Skeleton
                      variant="rect"
                      width={"100%"}
                      height={"auto"}
                      style={{ padding: "100% 0 0 0" }}
                      animation={false}
                    />
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-lg text-center w-full px-8">
                      Rất tiếc! <br />
                      <span className="text-sm">
                        Chúng tôi chưa tìm thấy KOL nào thỏa yêu cầu của bạn
                      </span>
                    </div>
                  </div>
                  <div className="w-full mt-3 flex flex-col">
                    <h3 className="text-lg font-bold line-clamp-1">
                      <Skeleton variant="text" animation={false} />
                    </h3>
                    <h4 className="text-xs text-grey-80 line-clamp-1">
                      <Skeleton variant="text" animation={false} />
                    </h4>
                  </div>
                  <div className="w-full mt-3">
                    <Skeleton
                      variant="rect"
                      width={"100%"}
                      height={"2rem"}
                      animation={false}
                    />
                  </div>
                </div>
              </div>
            ) : (
              profiles?.map((i: IProfile) => (
                <ProfileCard
                  key={i.id}
                  platform={filter.platform}
                  onClick={onCardClick.bind(null, i)}
                  {...i}
                />
              ))
            )}
            {!isReachingEnd && <HomeSkeleton needItem={12} ref={loadMoreRef} />}
          </div>
        )}
        <ProfileModal
          profile={profile}
          isShow={!!profile}
          handleClose={() => setProfile(null)}
        />
      </div>
    </>
  );
};

export default Search;
